import { useMutation } from "@tanstack/react-query";
import type {
  LoginAdministratorRequest,
  LoginLifeguardRequest,
  LifeguardPreLoginRequest,
  LoginResponse,
  VerifyUserRequest,
} from "../../../api/client/types/auth.types";
import { authEndpoints } from "../../../api/endpoints/auth";
import { useAuthStore } from "../../../store";
import type { ApiError } from "../../../api/client/types/common.types";

/**
 * React Query Keys
 * Organized keys help with cache invalidation and devtools debugging.
 */
const authQueryKeys = {
  root: ["auth"] as const,
  verifyUser: () => [...authQueryKeys.root, "verify-user"] as const,
  loginAdministrator: () => [...authQueryKeys.root, "login-admin"] as const,
  preLoginLifeguard: () =>
    [...authQueryKeys.root, "pre-login-lifeguard"] as const,
  loginLifeguard: () => [...authQueryKeys.root, "login-lifeguard"] as const,
};

/**
 * Hook: useVerifyUser
 * Sends a POST request to verify if a user exists
 */
export const useVerifyUser = () => {
  return useMutation<boolean, ApiError, VerifyUserRequest>({
    mutationKey: authQueryKeys.verifyUser(),
    mutationFn: (data) => authEndpoints.verifyUser(data),
  });
};

/**
 * Hook: useLoginAdministrator
 * Authenticates an administrator and returns tokens/user info.
 */

// Login Administrator
export const useLoginAdministrator = () => {
  const login = useAuthStore((state) => state.login);

  return useMutation<LoginResponse, ApiError, LoginAdministratorRequest>({
    mutationKey: authQueryKeys.loginAdministrator(),
    mutationFn: (data) => authEndpoints.loginAdministrator(data),
    onSuccess: (response) => {
      // Save token in Zustand + cookie
      login(response);
    },
  });
};

/**
 * Hook: usePreLoginLifeguard
 * Pre-login step for lifeguards.
 * Verifies the lifeguard's identity and returns a token containing only the user ID.
 * This token can be used to fetch available buildings before final login with a specific building.
 */
export const usePreLoginLifeguard = () => {
  const preLogin = useAuthStore((state) => state.preLogin);

  return useMutation<LoginResponse, ApiError, LifeguardPreLoginRequest>({
    mutationKey: authQueryKeys.preLoginLifeguard(),
    mutationFn: (data) => authEndpoints.preLoginLifeguard(data),
    onSuccess: (response) => {
      // Save token and set pre-authenticated state
      preLogin(response);
    },
  });
};

/**
 * Hook: useLoginLifeguard
 * Complete lifeguard login with building selection.
 * Requires a valid pre-login token (user already authenticated).
 * The pre-login token should be saved in the store (via usePreLoginLifeguard),
 * and the axios interceptor will automatically add it to the request headers.
 */
export const useLoginLifeguard = () => {
  const login = useAuthStore((state) => state.login);

  return useMutation<LoginResponse, ApiError, LoginLifeguardRequest>({
    mutationKey: authQueryKeys.loginLifeguard(),
    mutationFn: (data) => authEndpoints.loginLifeguard(data),
    onSuccess: (response) => {
      // Save token in Zustand + cookie
      login(response);
    },
  });
};
